/**
 * Enganche con HILO: subir la grabación nativa de iOS enlazada a la llamada.
 * (docs/INTEGRACION-SIM-BRIDGE.md de HILO, cambio #1 del puente.)
 * Config por equipo en localStorage: hilo:base + hilo:token (token de subida
 * hilo_up_…, solo abre /v1/calls/import). Sin config, la pantalla no muestra nada.
 */

export interface HiloConfig {
  base: string;
  token: string;
}

export function configHilo(storage: Storage = localStorage): HiloConfig | null {
  try {
    const base = (storage.getItem('hilo:base') ?? '').trim().replace(/\/+$/, '');
    const token = (storage.getItem('hilo:token') ?? '').trim();
    if (!/^https?:\/\//.test(base) || !token.startsWith('hilo_up_')) return null;
    return { base, token };
  } catch {
    return null;
  }
}

const MENSAJES: Record<string, string> = {
  archivo_no_es_audio: 'Ese archivo no es una grabación de audio. Comparte el .m4a de Notas → Grabaciones de llamadas.',
  archivo_vacio: 'El archivo llegó vacío. Vuelve a elegir la grabación.',
  grabacion_demasiado_corta: 'La grabación dura menos de 3 segundos: no hay conversación que analizar.',
  archivo_demasiado_grande: 'La grabación supera el tamaño máximo de HILO. Súbela desde el panel.',
  tope_minutos_llamada: 'La grabación supera el tope de minutos por llamada de HILO.',
  grabacion_de_otro_cliente: 'Esa grabación ya está registrada en la ficha de OTRO cliente. Revisa el archivo elegido.',
  cliente_en_no_contactar: 'Ese cliente pidió no ser contactado: HILO no procesa su grabación.',
  token_invalido: 'La credencial de HILO de este equipo no vale. Pide al administrador un token de subida nuevo.',
  token_fuera_de_alcance: 'La credencial de HILO de este equipo no vale. Pide al administrador un token de subida nuevo.',
};

export interface SubidaGrabacion {
  duplicada: boolean;
  callId: string | null;
}

export async function subirGrabacion(
  config: HiloConfig,
  archivo: File,
  referencia: { externoId?: string | null; nota?: string },
): Promise<SubidaGrabacion> {
  const form = new FormData();
  form.append('file', archivo, archivo.name || 'grabacion.m4a');
  if (referencia.externoId) {
    form.append('externo_sistema', 'bitrix_sim_bridge');
    form.append('externo_id', referencia.externoId);
  }
  if (referencia.nota) form.append('note', referencia.nota);

  let respuesta: Response;
  try {
    respuesta = await fetch(`${config.base}/v1/calls/import`, {
      method: 'POST',
      headers: { authorization: `Bearer ${config.token}` },
      body: form,
    });
  } catch {
    throw new Error('No se pudo llegar a HILO. Revisa la conexión y vuelve a intentarlo: reintentar no duplica.');
  }
  let body: {
    duplicado?: boolean;
    call?: { id?: string };
    error?: { code?: string; message?: string };
  } | null = null;
  try { body = await respuesta.json(); } catch { /* se decide por el status */ }

  if (respuesta.ok) {
    return { duplicada: body?.duplicado === true, callId: body?.call?.id ?? null };
  }
  const code = body?.error?.code ?? '';
  throw new Error(
    MENSAJES[code]
      ?? (body?.error?.message
        ? `HILO no aceptó la grabación: ${body.error.message}`
        : `HILO no aceptó la grabación (${respuesta.status}). Vuelve a intentarlo.`),
  );
}
